import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Cpu, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useCustomers } from '@/hooks/useCustomers';
import { kernelStatusKeys, useKernelStatus } from '@/hooks/useKernelStatus';
import type { KernelStatusRow } from '@/hooks/useKernelStatus';
import { ApiError } from '@/lib/api';

/**
 * /systems/kernel-status - laufender Kernel laut Zabbix, abgeglichen
 * gegen den Zielkernel. 503 heißt "Zabbix nicht konfiguriert", nicht
 * "keine Hosts" - das darf nicht in der leeren Tabelle untergehen.
 */

const DEFAULT_KERNEL_TARGET = '6.12.107';

const KERNEL_COLUMNS: ReadonlyArray<Column<KernelStatusRow>> = [
  {
    id: 'kunde',
    header: 'Kunde',
    filterValue: (row) => `${row.customer_name} ${row.customer_slug}`,
    cell: (row) => (
      <>
        <div className="font-medium">{row.customer_name}</div>
        <div className="font-mono text-xs text-muted">{row.customer_slug}</div>
      </>
    ),
  },
  {
    id: 'host',
    header: 'Host',
    className: 'font-mono text-xs',
    cell: (row) => row.hostname,
  },
  {
    id: 'typ',
    header: 'Typ',
    filter: 'select',
    filterValue: (row) => row.service_type ?? '',
    cell: (row) => row.service_type ?? '–',
  },
  {
    id: 'os',
    header: 'OS',
    filterValue: (row) => row.os_name ?? '',
    cell: (row) => row.os_name ?? '–',
  },
  {
    id: 'laufender-kernel',
    header: 'laufender Kernel',
    className: 'font-mono text-xs',
    filterValue: (row) => row.kernel_running ?? '',
    cell: (row) => row.kernel_running ?? '–',
  },
  {
    id: 'ziel-erreicht',
    header: 'Ziel erreicht',
    filter: 'select',
    filterValue: (row) =>
      row.kernel_ok === true ? 'ja' : row.kernel_ok === false ? 'nein' : 'unbekannt',
    cell: (row) => <KernelOkBadge ok={row.kernel_ok} />,
  },
  {
    id: 'reboot-noetig',
    header: 'Reboot nötig',
    filter: 'select',
    filterValue: (row) =>
      row.reboot_required === true
        ? 'Reboot nötig'
        : row.reboot_required === false
          ? 'nein'
          : '',
    cell: (row) =>
      row.reboot_required === true ? (
        <Badge tone="warning">Reboot nötig</Badge>
      ) : row.reboot_required === false ? (
        <span className="text-xs text-muted">nein</span>
      ) : (
        <span className="text-xs text-muted">–</span>
      ),
  },
  {
    id: 'security-updates',
    header: 'Security-Updates',
    className: 'tabular-nums',
    filterValue: (row) => (row.security_updates == null ? '' : String(row.security_updates)),
    cell: (row) => (row.security_updates == null ? '–' : row.security_updates),
  },
  {
    id: 'zuletzt-gesehen',
    header: 'zuletzt gesehen',
    filterValue: (row) => row.last_seen ?? '',
    cell: (row) => <RelativeTime value={row.last_seen} />,
  },
];

export const Route = createFileRoute('/systems/kernel-status')({
  component: KernelStatusPage,
});

function KernelStatusPage(): JSX.Element {
  const qc = useQueryClient();
  const [customerSlug, setCustomerSlug] = useState<string | null>(null);
  const [onlyPending, setOnlyPending] = useState(false);
  const [kernelTarget, setKernelTarget] = useState(DEFAULT_KERNEL_TARGET);

  const customersQuery = useCustomers({ limit: 200 });
  const filter = {
    ...(customerSlug ? { customerSlug } : {}),
    ...(onlyPending ? { onlyPending: true } : {}),
    ...(kernelTarget.trim() !== '' ? { kernelTarget: kernelTarget.trim() } : {}),
  };
  const statusQuery = useKernelStatus(filter);

  const customerOptions = useMemo(
    () =>
      (customersQuery.data?.data ?? [])
        .filter((c) => !c.deactivated_at)
        .map((c) => ({ value: c.slug, label: c.name, hint: c.slug })),
    [customersQuery.data]
  );

  const error = statusQuery.error;
  const zabbixMissing = error instanceof ApiError && error.status === 503;
  const rows = statusQuery.data?.rows ?? [];
  const summary = statusQuery.data?.summary;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Kernel-Stand</h1>
          <p className="text-muted">
            Laufender Kernel aus Zabbix, abgeglichen gegen den Zielkernel.
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="size-4" />}
          onClick={() => {
            void qc.invalidateQueries({ queryKey: kernelStatusKeys.all });
          }}
          data-testid="kernel-reload"
        >
          Neu laden
        </Button>
      </header>

      <Card title="Filter">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Combobox
              label="Kunde"
              value={customerSlug}
              onChange={setCustomerSlug}
              options={customerOptions}
              placeholder="Alle Kunden"
            />
          </div>
          <div className="min-w-[180px]">
            <Input
              label="Zielkernel"
              value={kernelTarget}
              onChange={(e) => setKernelTarget(e.target.value)}
              data-testid="kernel-target"
            />
          </div>
          <label className="mb-1 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyPending}
              onChange={(e) => setOnlyPending(e.target.checked)}
              className="size-4 accent-primary"
              data-testid="kernel-only-pending"
            />
            nur ausstehend
          </label>
        </div>
      </Card>

      {zabbixMissing && (
        <div
          role="alert"
          className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
          data-testid="kernel-zabbix-missing"
        >
          <p className="font-medium">Zabbix ist nicht konfiguriert.</p>
          <p className="mt-1 text-muted">
            Kernel-Stand kommt aus der Zabbix-Integration (HTTP 503). Ohne
            konfigurierte Verbindung gibt es hier keine Hosts zu zeigen.
          </p>
        </div>
      )}

      {error && !zabbixMissing && <ErrorAlert error={error} />}

      {summary && (
        <div
          className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7"
          data-testid="kernel-summary"
        >
          <StatCard label="Hosts" value={summary.hosts_total} testId="kernel-summary-total" />
          <StatCard label="zugeordnet" value={summary.matched} />
          <StatCard label="ohne Match" value={summary.unmatched} />
          <StatCard label="Ziel ok" value={summary.ok} tone="success" />
          <StatCard label="nicht ok" value={summary.not_ok} tone="danger" />
          <StatCard label="Reboot" value={summary.reboot_required} tone="warning" />
          <StatCard label="unbekannt" value={summary.unknown} />
        </div>
      )}

      <Card title="Hosts">
        {statusQuery.isLoading && <SkeletonRows rows={5} cols={8} />}
        {!statusQuery.isLoading && !zabbixMissing && rows.length === 0 && !error && (
          <EmptyState
            icon={<Cpu className="size-8" />}
            title="Keine Hosts"
            description="Filter prüfen oder Zabbix-Daten abwarten."
          />
        )}
        {rows.length > 0 && (
          <DataTable
            caption="Kernel-Stand"
            columns={KERNEL_COLUMNS}
            rows={rows}
            rowKey={(row) => row.system_id}
            storageKey="kernel-status"
            rowTestId={(row) => `kernel-row-${row.system_id}`}
          />
        )}
      </Card>
    </div>
  );
}

function KernelOkBadge({ ok }: { ok: boolean | null }): JSX.Element {
  if (ok === true) return <Badge tone="success">ja</Badge>;
  if (ok === false) return <Badge tone="danger">nein</Badge>;
  return <Badge tone="neutral">unbekannt</Badge>;
}
